import { useEffect } from 'react';
import type { JSX } from 'react';
import { injectHeadScript } from '../util/inject-head-script';
import {
    claimBoot,
    getAdvertisingWindow,
    type AdvertisingWindow,
    type PintrkStub,
} from '../util/advertising-window';
import { isConfiguredPixelId } from './is-configured-pixel-id';
/* eslint-disable jsx-a11y/alt-text */

const PINTEREST_SRC = 'https://s.pinimg.com/ct/core.js';

interface PinterestTrackerProps {
    pixelArchipro: string;
    pixelClient: string;
}

const getOrCreatePintrk = (w: AdvertisingWindow): PintrkStub => {
    const existing = w.pintrk;
    if (existing) return existing;

    function pintrk(...args: unknown[]): void {
        pintrk.queue.push(args);
    }

    pintrk.queue = [] as unknown[][];
    pintrk.version = '3.0';

    w.pintrk = pintrk;
    return pintrk;
};

/**
 * Boot the Pinterest tag for every configured id.
 *
 * The page view follows the loads so it is attributed to both tags, and is sent
 * only alongside a first load, so it stays one page view per document.
 */
export const loadPinterestTag = (tagIds: string[]): void => {
    const w = getAdvertisingWindow();
    const ids = tagIds
        .filter(isConfiguredPixelId)
        .filter((id) => claimBoot(w, `pinterest:${id}`));
    if (ids.length === 0) return;

    const pintrk = getOrCreatePintrk(w);
    for (const id of ids) {
        pintrk('load', id);
    }
    if (claimBoot(w, 'pinterest:page')) {
        pintrk('page');
    }

    injectHeadScript(PINTEREST_SRC);
};

export const PinterestTrackerHead: React.FC<PinterestTrackerProps> = ({
    pixelArchipro,
    pixelClient,
}) => {
    // Injected after hydration (see `injectHeadScript`).
    useEffect(() => {
        loadPinterestTag([pixelArchipro, pixelClient]);
    }, [pixelArchipro, pixelClient]);

    return null;
};

export const PinterestTrackerBody = ({
    pixelArchipro,
    pixelClient,
}: PinterestTrackerProps): JSX.Element => {
    const tagIds = [pixelArchipro, pixelClient].filter(isConfiguredPixelId);

    return (
        <noscript>
            {tagIds.map((id) => (
                <img
                    key={id}
                    height="1"
                    width="1"
                    style={{ display: 'none' }}
                    alt=""
                    src={`https://ct.pinterest.com/v3/?event=init&tid=${id}&noscript=1`}
                />
            ))}
        </noscript>
    );
};
